/**
 * A register narrowed to one project (wave b12 frontend item 12, Mahmoud's finding on
 * the live home screen of 2026-10-25): each quality figure on a Home project card links
 * to the register it counts, pre-filtered to that project, and the register says so.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The figures are the server's (portfolio/home.py): open findings and open punch items
 * by the finding's project, inspections in progress and open RFIs by the project's
 * contracts. The register is narrowed by the same rules, so the figure and the list
 * agree: the findings register asks the server for the project (`project_id`), and the
 * inspection and RFI registers, whose routes take a contract and not a project, keep the
 * rows of the project's contracts (WAVE_NOTES.md, Backend follow-ups).
 */

import { ListFilter } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import { listContracts } from "../api/portfolio";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { useQuery } from "./useQuery";

/** The states a finding or a punch item is over in (portfolio/home.py FINDING_DONE...). */
export const FINDING_DONE: readonly string[] = ["CLOSED", "RECORDED"];
export const PUNCH_DONE: readonly string[] = ["CLOSED", "PUNCH_COMPLETED"];
export const PUNCH_STATES: readonly string[] = ["IN_PUNCH_LIST", "PUNCH_COMPLETED"];
/** The states an inspection is over in: closed, rejected, cancelled or waived. */
export const INSPECTION_DONE: readonly string[] = ["CLOSED", "REJECTED", "CANCELLED", "WAIVED"];

/** A finding is a punch item when it sits on a punch list or reads a punch state. */
export function isPunchItem(row: {
  readonly punchListId: string | null;
  readonly findingStatus: string;
}): boolean {
  return row.punchListId !== null || PUNCH_STATES.includes(row.findingStatus);
}

/** The query a Home figure links with: the project, its name for the banner, the view. */
export function projectFilterSearch(
  project: { readonly id: string; readonly humanId: string },
  view: Readonly<Record<string, string>>,
): string {
  const params = new URLSearchParams({ project: project.id, project_label: project.humanId });
  for (const [name, value] of Object.entries(view)) {
    params.set(name, value);
  }
  return `?${params.toString()}`;
}

/** The project a register is narrowed to, from its URL; null for the whole register. */
export function useProjectFilter(): { readonly id: string; readonly label: string } | null {
  const [params] = useSearchParams();
  const id = params.get("project");
  if (id === null || id === "") {
    return null;
  }
  return { id, label: params.get("project_label") ?? strings.projectFilter.unnamed };
}

/**
 * The ids of the project's contracts, for a register whose route takes a contract: null
 * while there is no project filter (keep every row), an empty set while it is read.
 */
export function useProjectContracts(projectId: string | null): ReadonlySet<string> | null {
  const token = useToken();
  const contracts = useQuery(
    (signal) =>
      projectId === null
        ? Promise.resolve(null)
        : listContracts(token, { projectId, signal }).then(
            (body) => new Set(body.items.map((one) => one.id)),
          ),
    [token, projectId],
  );
  if (projectId === null) {
    return null;
  }
  return contracts.kind === "ready" && contracts.data !== null ? contracts.data : new Set();
}

/** The banner over a narrowed register: what it shows, and the way back to all of it. */
export function ProjectFilterBanner({
  label,
  view,
  register,
}: {
  readonly label: string;
  /** What the figure counted, in words ("Open findings"). */
  readonly view: string;
  /** The whole register's route. */
  readonly register: string;
}): React.JSX.Element {
  return (
    <div
      className="q-ui mb-(--q-s1) flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-gold-soft bg-gold-wash px-(--q-s0) py-(--q-s-1)"
      role="status"
      data-testid="project-filter-banner"
    >
      <ListFilter aria-hidden="true" className="size-4 shrink-0 text-gold" />
      <p className="min-w-0 flex-1 text-sm text-foreground">
        <strong>{strings.projectFilter.banner(view, label)}</strong>
      </p>
      <Link to={register} className="q-link text-sm" data-testid="project-filter-show-all">
        {strings.projectFilter.showAll}
      </Link>
    </div>
  );
}
